import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Upload, X, Youtube } from "lucide-react";
import { adminApi } from "../../pages/Segreteria";
import { inputCls, labelCls } from "../legend/ContactForm";
import { detectVideoLink, mediaUrl } from "../legend/PageShell";

/** Caricamento file (foto/video) o aggiunta link YouTube, con anteprima e didascalia. */
export const MediaList = ({ label, items, onChange, allowYoutube = true, testid }) => {
  const [busy, setBusy] = useState(false);
  const [yt, setYt] = useState("");

  const upload = async (files) => {
    setBusy(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await adminApi.post("/admin/media", fd);
        added.push(data);
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "caricamento fallito"}`);
      }
    }
    onChange([...items, ...added]);
    setBusy(false);
    if (added.length) toast.success(`${added.length} file caricati`);
  };

  const update = (i, patch) => onChange(items.map((m, k) => (k === i ? { ...m, ...patch } : m)));

  return (
    <div data-testid={testid} className="border border-neutral-800 p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={labelCls}>{label}</span>
        <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-1.5 border border-neutral-700 hover:border-red-600 mono-label text-[10px] uppercase text-neutral-200">
          <Upload className="w-3.5 h-3.5" /> {busy ? "Caricamento..." : "Carica file"}
          <input type="file" multiple accept="image/*,video/*" className="sr-only" disabled={busy} onChange={(e) => upload([...e.target.files])} data-testid={`${testid}-file`} />
        </label>
      </div>
      {allowYoutube && (
        <div className="flex gap-2">
          <input className={inputCls} placeholder="Link YouTube o Instagram (reel)" value={yt} onChange={(e) => setYt(e.target.value)} data-testid={`${testid}-yt`} />
          <button type="button" className="px-3 border border-neutral-700 hover:border-red-600 text-neutral-200" onClick={() => { const tipo = detectVideoLink(yt); if (!tipo) { toast.error("Incolla un link YouTube o Instagram valido"); return; } onChange([...items, { url: yt, tipo, didascalia: "" }]); setYt(""); }} aria-label="Aggiungi link video"><Youtube className="w-4 h-4" /></button>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((m, i) => (
          <div key={i} className="border border-neutral-800 bg-neutral-900 p-2 space-y-1">
            <div className="aspect-video bg-black flex items-center justify-center overflow-hidden">
              {m.tipo === "image" ? <img src={mediaUrl(m.url)} alt="" className="w-full h-full object-contain" /> : <span className="mono-label text-[10px] uppercase text-neutral-400">{m.tipo}</span>}
            </div>
            <input className="w-full bg-neutral-950 border border-neutral-800 p-1.5 text-[11px] text-white" placeholder="Didascalia" value={m.didascalia || ""} onChange={(e) => update(i, { didascalia: e.target.value })} />
            <button type="button" className="w-full inline-flex items-center justify-center gap-1 text-[10px] uppercase text-neutral-500 hover:text-red-500" onClick={() => onChange(items.filter((_, k) => k !== i))}><X className="w-3 h-3" /> Rimuovi</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (<div><span className={labelCls}>{label}</span>{children}</div>);

const EMPTY_TEAM = { nome: "", instagram: "", ruolo: "", foto: [] };

/** Team e piloti ospiti della gara: nome, Instagram, ruolo e foto dedicate. */
const TeamList = ({ items, onChange }) => {
  const update = (i, patch) => onChange(items.map((t, k) => (k === i ? { ...t, ...patch } : t)));
  return (
    <div data-testid="cms-team" className="border border-neutral-800 p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={labelCls}>Team e piloti che hanno corso con noi</span>
        <button type="button" data-testid="cms-team-add" onClick={() => onChange([...items, { ...EMPTY_TEAM }])} className="inline-flex items-center gap-2 px-3 py-1.5 border border-neutral-700 hover:border-red-600 mono-label text-[10px] uppercase text-neutral-200"><Plus className="w-3.5 h-3.5" /> Aggiungi team</button>
      </div>
      {items.map((t, i) => (
        <div key={i} data-testid={`cms-team-${i}`} className="border border-neutral-800 bg-neutral-950 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input className={inputCls} placeholder="Nome team / pilota *" value={t.nome} onChange={(e) => update(i, { nome: e.target.value })} required data-testid={`cms-team-${i}-nome`} />
            <input className={inputCls} placeholder="Instagram (es. endrift_garage)" value={t.instagram} onChange={(e) => update(i, { instagram: e.target.value })} data-testid={`cms-team-${i}-instagram`} />
            <input className={inputCls} placeholder="Ruolo / note (es. Team drift)" value={t.ruolo} onChange={(e) => update(i, { ruolo: e.target.value })} />
          </div>
          <MediaList testid={`cms-team-${i}-foto`} label={`Foto ${t.nome || "team"}`} allowYoutube={false} items={t.foto} onChange={(v) => update(i, { foto: v })} />
          <button type="button" className="inline-flex items-center gap-1 text-[10px] uppercase text-neutral-500 hover:text-red-500" onClick={() => onChange(items.filter((_, k) => k !== i))}><X className="w-3 h-3" /> Rimuovi team</button>
        </div>
      ))}
    </div>
  );
};

const EMPTY_EVENTO = { titolo: "", anno: 2026, luogo: "", data: "", descrizione: "", copertina: "", loghi_organizzatori: [], loghi_comuni: [], media: [], team: [], pubblicato: true };
const EMPTY_INTERVISTA = { titolo: "", didascalia: "", video: null, copertina: "", articolo: "", autore: "Redazione Legend Magazine", pubblicato: true };

/** Editor generico lista + form per eventi (gare) e interviste. */
export const CmsManager = ({ kind }) => {
  const isEvento = kind === "eventi";
  const [list, setList] = useState([]);
  const [item, setItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => adminApi.get(`/admin/${kind}`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (item.id) await adminApi.put(`/admin/${kind}/${item.id}`, item);
      else await adminApi.post(`/admin/${kind}`, item);
      toast.success("Pagina salvata e pubblicata");
      setItem(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa pagina?")) return;
    await adminApi.delete(`/admin/${kind}/${id}`);
    toast.success("Pagina eliminata");
    load();
  };
  const set = (patch) => setItem({ ...item, ...patch });

  return (
    <div data-testid={`cms-${kind}`} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-neutral-400">{isEvento ? "Pagine dedicate a luoghi ed eventi dei rally: foto, video, loghi organizzatori e comuni. Compaiono in home (bottoni galleria + barra Ultime gare) e in /gare." : "Video interviste pubblicate nel feed social /interviste con articolo firmato Redazione Legend Magazine. Compaiono nella barra in home."}</p>
        <button type="button" data-testid={`cms-${kind}-new`} onClick={() => setItem(isEvento ? { ...EMPTY_EVENTO } : { ...EMPTY_INTERVISTA })} className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white mono-label text-[11px] font-black uppercase tracking-wider"><Plus className="w-4 h-4" /> Nuova pagina</button>
      </div>

      {item && (
        <form onSubmit={save} data-testid={`cms-${kind}-form`} className="border border-neutral-800 bg-neutral-900 p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Titolo *"><input className={inputCls} value={item.titolo} onChange={(e) => set({ titolo: e.target.value })} required minLength={2} data-testid="cms-titolo" /></Field>
            {isEvento ? (
              <>
                <Field label="Anno"><input type="number" className={inputCls} value={item.anno} onChange={(e) => set({ anno: Number(e.target.value) })} /></Field>
                <Field label="Luogo"><input className={inputCls} value={item.luogo} onChange={(e) => set({ luogo: e.target.value })} /></Field>
                <Field label="Data (testo)"><input className={inputCls} value={item.data} onChange={(e) => set({ data: e.target.value })} placeholder="es. 14-15 marzo 2026" /></Field>
              </>
            ) : (
              <Field label="Didascalia (post social)"><input className={inputCls} value={item.didascalia} onChange={(e) => set({ didascalia: e.target.value })} /></Field>
            )}
          </div>
          {isEvento ? (
            <>
              <Field label="Descrizione"><textarea rows="3" className={inputCls} value={item.descrizione} onChange={(e) => set({ descrizione: e.target.value })} /></Field>
              <MediaList testid="cms-loghi-org" label="Loghi organizzatori" allowYoutube={false} items={item.loghi_organizzatori} onChange={(v) => set({ loghi_organizzatori: v })} />
              <MediaList testid="cms-loghi-comuni" label="Loghi comuni" allowYoutube={false} items={item.loghi_comuni} onChange={(v) => set({ loghi_comuni: v })} />
              <MediaList testid="cms-media" label="Foto e video della gara" items={item.media} onChange={(v) => set({ media: v })} />
              <TeamList items={item.team || []} onChange={(v) => set({ team: v })} />
            </>
          ) : (
            <>
              <MediaList testid="cms-video" label="Video intervista (file o YouTube) — viene usato il primo" items={item.video ? [item.video] : []} onChange={(v) => set({ video: v[0] || null })} />
              <Field label="Articolo (firmato Redazione Legend Magazine)"><textarea rows="8" className={inputCls} value={item.articolo} onChange={(e) => set({ articolo: e.target.value })} data-testid="cms-articolo" /></Field>
              <Field label="Firma"><input className={inputCls} value={item.autore} onChange={(e) => set({ autore: e.target.value })} /></Field>
            </>
          )}
          <label className="inline-flex items-center gap-2 text-xs text-neutral-300"><input type="checkbox" checked={item.pubblicato} onChange={(e) => set({ pubblicato: e.target.checked })} /> Pubblicata (visibile sul sito)</label>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} data-testid="cms-save" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-red-600 hover:text-white text-black mono-label text-[11px] font-black uppercase tracking-wider disabled:opacity-50"><Save className="w-4 h-4" /> {saving ? "Salvataggio..." : "Salva pagina"}</button>
            <button type="button" onClick={() => setItem(null)} className="px-5 py-2.5 border border-neutral-700 text-neutral-300 mono-label text-[11px] uppercase">Annulla</button>
          </div>
        </form>
      )}

      <ul className="divide-y divide-neutral-900 border border-neutral-800">
        {list.map((it) => (
          <li key={it.id} data-testid={`cms-row-${it.slug}`} className="flex flex-wrap items-center justify-between gap-3 p-4 hover:bg-neutral-900/60">
            <div className="min-w-0">
              <div className="font-bold text-white uppercase text-sm">{it.titolo} {!it.pubblicato && <span className="mono-label text-[10px] text-amber-400">bozza</span>}</div>
              <a href={isEvento ? `/gare/${it.slug}` : `/interviste/${it.slug}`} target="_blank" rel="noopener noreferrer" className="mono-label text-[10px] text-neutral-500 hover:text-red-500">/{isEvento ? "gare" : "interviste"}/{it.slug}</a>
              {isEvento && <span className="mono-label text-[10px] text-neutral-500 ml-3">{it.media?.length || 0} media · {it.loghi_organizzatori?.length || 0}+{it.loghi_comuni?.length || 0} loghi</span>}
            </div>
            <div className="flex gap-2">
              <button type="button" data-testid={`cms-edit-${it.slug}`} onClick={() => setItem(it)} className="px-3 py-1.5 border border-neutral-700 hover:border-red-600 text-neutral-200 mono-label text-[10px] uppercase">Modifica</button>
              <button type="button" onClick={() => remove(it.id)} className="px-3 py-1.5 border border-neutral-800 text-neutral-500 hover:text-red-500" aria-label="Elimina"><Trash2 className="w-4 h-4" /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
